import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Feather, Download, Share2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { BACKEND_URL } from '../../utils/api';
import { exportBookAsFlipbookPdf } from '../../utils/bookPdf';
import { WaxSeal } from './WaxSeal';

/* ═══════════════════════════════════════════════════════════════════
   BLISS BOOK — read-only book viewer for the user's sealed entries
   inside the Bliss Gallery.

   mode='quote'  → Royal Quote book (entries from /api/quotes/feed)
   mode='script' → Empire Script book (entries from /api/notebooks/feed/note)

   Pages are presented OLDEST → NEWEST (the order they were sealed),
   so the reader literally walks through the user's history from the
   beginning. No compose, no edit, no delete — purely a memory volume.
   Cover styling matches the Royal Quote Book / Empire Script editor.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#D4AF37';
const GOLD_LIGHT = '#F5D97A';
const INK = '#1a1208';

/* Theme palette per mode (reuses the cover/spine treatment from
   QuoteBook.jsx (oxblood) and NotebookEditor.jsx (sapphire)). */
const MODE_THEME = {
  quote: {
    title: 'The Royal Quotes',
    subTitle: 'Royal Quote',
    headerLabel: 'Royal Quote Book',
    coverGrad:
      'radial-gradient(ellipse at 50% 38%, #6e1620 0%, #4a0a14 45%, #2a040a 100%)',
    pageHeader: '~ A Royal Decree ~',
    emptyHint: 'Inscribe your first decree from the Try tab.',
    feedUrl: `${BACKEND_URL}/api/quotes/feed?limit=200`,
    pageKind: 'quote',
  },
  script: {
    title: 'The Empire Journal',
    subTitle: 'Empire Journal',
    headerLabel: 'Empire Journal',
    coverGrad:
      'radial-gradient(ellipse at 50% 38%, #173a7a 0%, #0a1f4a 50%, #050b1f 100%)',
    pageHeader: '~ A Sealed Entry ~',
    emptyHint: 'Inscribe your first entry from the Empire Journal tile.',
    feedUrl: `${BACKEND_URL}/api/notebooks/feed/note?limit=200`,
    pageKind: 'script',
  },
  ledger: {
    title: 'The Ledger of Souls',
    subTitle: 'Imperial Ledger',
    headerLabel: 'Imperial Ledger',
    coverGrad:
      'radial-gradient(ellipse at 50% 38%, #5a2a10 0%, #3a1808 50%, #1a0805 100%)',
    pageHeader: '~ A Soul Inscribed ~',
    emptyHint: 'Inscribe your first soul from the Imperial Ledger tile.',
    feedUrl: `${BACKEND_URL}/api/ledger`,
    pageKind: 'ledger',
  },
};

/* Sovereign Pillar themes — 8 chronicle books, one per chapter. */
const PILLAR_CHAPTERS = [
  { id: 'fashion', label: 'Fashion', cover: 'radial-gradient(ellipse at 50% 38%, #5a4310 0%, #332608 50%, #1a1305 100%)' },
  { id: 'food',    label: 'Taste',   cover: 'radial-gradient(ellipse at 50% 38%, #5a4810 0%, #2c2410 50%, #16120a 100%)' },
  { id: 'explore', label: 'Explore', cover: 'radial-gradient(ellipse at 50% 38%, #143578 0%, #0c1d3c 50%, #050d1e 100%)' },
  { id: 'souls',   label: 'Soul',    cover: 'radial-gradient(ellipse at 50% 38%, #6a1830 0%, #3a0a14 50%, #1a0508 100%)' },
  { id: 'sporty',  label: 'Sport',   cover: 'radial-gradient(ellipse at 50% 38%, #16604a 0%, #0a2a20 50%, #051410 100%)' },
  { id: 'success', label: 'Success', cover: 'radial-gradient(ellipse at 50% 38%, #4a4a52 0%, #1f1f24 50%, #0a0a0d 100%)' },
  { id: 'legacy',  label: 'Legacy',  cover: 'radial-gradient(ellipse at 50% 38%, #4a2a78 0%, #221534 50%, #100820 100%)' },
  { id: 'art',     label: 'Art',     cover: 'radial-gradient(ellipse at 50% 38%, #143580 0%, #0a1530 50%, #050a1a 100%)' },
];
PILLAR_CHAPTERS.forEach((p) => {
  MODE_THEME[`pillar-${p.id}`] = {
    title: `Pillar of ${p.label}`,
    subTitle: p.label,
    headerLabel: `${p.label} Chronicle`,
    coverGrad: p.cover,
    pageHeader: `~ ${p.label} ~`,
    emptyHint: `Add your first ${p.label.toLowerCase()} memory from the Try tab.`,
    feedUrl: `${BACKEND_URL}/api/memories?category=${p.id}`,
    pageKind: 'memory',
  };
});

export const BOOK_MODES = ['quote', 'script', 'ledger', ...PILLAR_CHAPTERS.map((p) => `pillar-${p.id}`)];
export { PILLAR_CHAPTERS, MODE_THEME };

/* Single page chrome — used by every page type (mirrors QuoteBook PageFrame) */
const PageFrame = ({ children }) => (
  <div
    className="absolute inset-0 flex flex-col"
    style={{
      background:
        'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
      borderRadius: '4px 14px 14px 4px',
      boxShadow:
        'inset 0 0 70px rgba(168,107,31,0.18), inset 0 0 12px rgba(82,46,8,0.5), 0 8px 28px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    }}
  >
    <div className="absolute inset-2 pointer-events-none"
      style={{ border: `2px solid ${GOLD}`, borderRadius: 6 }} />
    <div className="absolute inset-3.5 pointer-events-none"
      style={{ border: `1px solid rgba(168,107,31,0.5)`, borderRadius: 4 }} />
    {['1.5rem', 'calc(100% - 1.5rem)'].map((y) =>
      ['1.5rem', 'calc(100% - 1.5rem)'].map((x) => (
        <span
          key={`${x}-${y}`}
          aria-hidden
          style={{
            position: 'absolute', left: x, top: y,
            width: 14, height: 14,
            transform: 'translate(-50%, -50%) rotate(45deg)',
            border: `1px solid ${GOLD}`,
            background: 'rgba(212,175,55,0.18)',
            pointerEvents: 'none',
          }}
        />
      ))
    )}
    <div aria-hidden style={{
      position: 'absolute', top: 0, bottom: 0, left: 0, width: 16,
      background: 'linear-gradient(90deg, rgba(0,0,0,0.45), transparent)',
      pointerEvents: 'none',
    }} />
    {children}
  </div>
);

/* Sapphire/oxblood leather cover — matches the QuoteBook / Empire-Script editor */
const CoverPage = ({ empireName, profilePicture, theme }) => {
  const initial = (empireName || 'A')[0].toUpperCase();
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        background: theme.coverGrad,
        borderRadius: '4px 14px 14px 4px',
        boxShadow:
          'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 18px rgba(0,0,0,0.7), 0 12px 32px rgba(0,0,0,0.7)',
      }}
    >
      {/* leather grain */}
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.04) 0%, transparent 30%),' +
            'radial-gradient(circle at 78% 28%, rgba(0,0,0,0.18) 0%, transparent 28%),' +
            'radial-gradient(circle at 30% 78%, rgba(0,0,0,0.22) 0%, transparent 30%),' +
            'radial-gradient(circle at 82% 76%, rgba(255,225,180,0.05) 0%, transparent 32%)',
          mixBlendMode: 'overlay',
          opacity: 0.85,
        }}
      />
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%)' }} />
      {/* gold double frame */}
      <div aria-hidden className="absolute inset-3 pointer-events-none"
        style={{
          border: `2.5px solid ${GOLD}`,
          borderRadius: 8,
          boxShadow: 'inset 0 0 0 1px rgba(255,241,184,0.4), 0 0 14px rgba(212,175,55,0.35)',
        }} />
      <div aria-hidden className="absolute inset-5 pointer-events-none"
        style={{ border: '1px solid rgba(212,175,55,0.55)', borderRadius: 5 }} />
      {/* corner medallions */}
      {[
        { top: 14, left: 14 }, { top: 14, right: 14 },
        { bottom: 14, left: 14 }, { bottom: 14, right: 14 },
      ].map((pos, i) => (
        <div key={i} aria-hidden className="absolute pointer-events-none"
          style={{
            ...pos, width: 22, height: 22, transform: 'rotate(45deg)',
            background: 'linear-gradient(135deg, #F5E6A0 0%, #D4AF37 45%, #8B6B1F 100%)',
            border: '1px solid rgba(60,40,8,0.6)',
            boxShadow: '0 0 8px rgba(212,175,55,0.55), inset 0 0 4px rgba(255,255,255,0.35)',
          }} />
      ))}
      {/* watermark + spine */}
      <img
        src="/icons/empire_seal.png"
        alt=""
        aria-hidden
        draggable={false}
        className="absolute pointer-events-none select-none"
        style={{
          width: '70%', height: 'auto',
          left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 0.07,
          filter: 'sepia(1) hue-rotate(-15deg) saturate(2.5) brightness(1.4)',
          mixBlendMode: 'screen',
        }}
      />
      <div aria-hidden style={{
        position: 'absolute', top: 0, bottom: 0, right: 0, width: 18,
        background: 'linear-gradient(270deg, rgba(0,0,0,0.6), transparent)',
        pointerEvents: 'none',
      }} />

      <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.42em] text-[11px] mb-6 relative z-10"
        style={{ color: '#F5E6A0', textShadow: '0 1px 0 rgba(0,0,0,0.45)' }}>
        ~ {theme.title} ~
      </p>

      {/* portrait cameo */}
      <div className="relative mb-6 z-10" style={{ width: 110, height: 110 }}>
        <div aria-hidden className="absolute inset-0 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(245,217,122,0.55) 0%, rgba(212,175,55,0) 65%)',
          filter: 'blur(8px)', transform: 'scale(1.35)',
        }} />
        <div className="absolute inset-0 rounded-full overflow-hidden" style={{
          border: '3px solid #D4AF37',
          boxShadow:
            'inset 0 0 0 2px rgba(255,241,184,0.7),' +
            '0 0 18px rgba(212,175,55,0.55),' +
            '0 6px 18px rgba(0,0,0,0.55)',
          background: profilePicture
            ? '#1a0608'
            : 'radial-gradient(circle at 50% 32%, #f6e6b6, #c69b3f 70%, #6b4612 100%)',
        }}>
          {profilePicture ? (
            <img src={profilePicture} alt={empireName} draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700,
              fontSize: 50, fontStyle: 'italic', color: '#3a2410',
              textShadow: '0 1px 0 rgba(255,241,184,0.6)',
            }}>{initial}</div>
          )}
        </div>
      </div>

      <h1 className="font-['Cormorant_Garamond'] font-bold leading-none relative z-10"
        style={{
          fontSize: 'clamp(40px, 12vw, 68px)', letterSpacing: '0.06em',
          background: 'linear-gradient(180deg, #FFF1B8 0%, #F5D97A 35%, #C69B3F 75%, #7A5418 100%)',
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          textShadow: '0 2px 0 rgba(0,0,0,0.4), 0 0 22px rgba(212,175,55,0.35)',
          filter: 'drop-shadow(0 1px 0 rgba(60,30,8,0.7))',
        }}>
        {empireName}
      </h1>

      <div className="my-5 w-32 h-[1.5px] relative z-10" style={{
        background: `linear-gradient(90deg, transparent, ${GOLD_LIGHT}, transparent)`,
        boxShadow: '0 0 8px rgba(245,217,122,0.5)',
      }} />

      <h2 className="font-['Cormorant_Garamond'] italic font-light relative z-10"
        style={{
          color: '#F5E6A0', fontSize: 'clamp(22px, 6vw, 30px)',
          letterSpacing: '0.08em', textShadow: '0 1px 0 rgba(0,0,0,0.5)',
        }}>
        {theme.title}
      </h2>

      <div className="mt-10 opacity-80 relative z-10">
        <Feather size={32} style={{ color: GOLD_LIGHT, filter: 'drop-shadow(0 0 6px rgba(245,217,122,0.5))' }} />
      </div>

      <p className="absolute bottom-7 left-0 right-0 text-center font-['Cormorant_Garamond'] italic text-[11px] z-10"
        style={{ color: 'rgba(245,230,160,0.65)' }}>
        — turn the page to read from the beginning —
      </p>
    </div>
  );
};

/* Read-only quote page — mirrors QuoteBook QuotePage */
const QuotePage = ({ q, author, signatureUrl, theme }) => (
  <PageFrame>
    <div className="relative flex-1 flex flex-col px-5 sm:px-7 pt-7 pb-5">
      <p
        className="font-['Cormorant_Garamond'] uppercase tracking-[0.32em] text-[10px] text-center"
        style={{ color: 'rgba(82,46,8,0.7)' }}>
        {theme.pageHeader}
      </p>
      <div className="mx-auto mt-2 mb-4 w-20 h-[1px]" style={{ background: GOLD }} />

      {q.vibe_date && (
        <h3
          className="font-['Cormorant_Garamond'] font-semibold text-center mb-3"
          style={{ color: INK, fontSize: 'clamp(20px, 5.5vw, 28px)' }}>
          {q.vibe_date}
        </h3>
      )}

      <p
        className="flex-1 font-['Cormorant_Garamond'] italic leading-relaxed text-center px-2 overflow-y-auto"
        style={{ color: '#2a0e0a', fontSize: 'clamp(16px, 4.5vw, 20px)' }}>
        &ldquo;{q.note || 'A timeless lesson.'}&rdquo;
      </p>

      <div className="mx-auto my-4 w-24 h-[1px]"
        style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />

      <div className="text-center">
        <p className="font-['Cormorant_Garamond'] italic text-[11px]" style={{ color: 'rgba(82,46,8,0.7)' }}>
          — sealed by
        </p>
        <p className="font-['Cormorant_Garamond'] font-bold tracking-wide"
          style={{ color: INK, fontSize: 'clamp(18px, 4.8vw, 22px)' }}>
          {author}
        </p>
        {q.created_at && (
          <p className="font-['Outfit'] text-[10px] mt-0.5" style={{ color: 'rgba(58,20,16,0.55)' }}>
            {new Date(q.created_at).toLocaleDateString('en-GB', {
              day: '2-digit', month: 'short', year: 'numeric',
            })}
          </p>
        )}
        <div className="mt-2 flex justify-center">
          <WaxSeal signatureUrl={signatureUrl} size={48} />
        </div>
      </div>
    </div>
  </PageFrame>
);

/* ────────────────────────── PRINT-ONLY VARIANTS ──────────────────────────
   These mirror the in-app pages but use the simplest possible CSS that
   html2canvas can render without quirks:
     • No `aspect-ratio` (use explicit pixel heights)
     • No `filter: blur()` (no blurred backdrops)
     • No `object-fit` (use explicit width/height on <img>)
     • No `position: absolute` images
   Used only inside the off-screen capture container so the live UI
   stays cinematic. */

const PrintMemoryPage = ({ entry, pillarId, idx, total }) => {
  const accent = PILLAR_ACCENT[pillarId] || GOLD;
  const label = PILLAR_LABEL[pillarId] || 'Path';
  const dt = entry?.created_at ? new Date(entry.created_at) : null;
  const dateStr = dt ? dt.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '';
  const passage = (entry.passage || '').trim();
  return (
    <div style={{
      width: '100%', height: '100%', boxSizing: 'border-box',
      padding: 16, position: 'relative',
      background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
      borderRadius: '4px 14px 14px 4px',
      border: `2px solid ${GOLD}`,
    }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.28em',
          color: 'rgba(82,46,8,0.85)', fontWeight: 600,
        }}>Index</span>
        <span style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.32em',
          color: accent,
        }}>{label} Path</span>
        <span style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontStyle: 'italic', fontSize: 11, color: 'rgba(82,46,8,0.6)',
        }}>{idx + 1} / {total}</span>
      </div>

      {/* image — explicit pixel height, plain img tag */}
      <div style={{
        marginTop: 12,
        width: '100%', height: 220,
        border: `2px solid ${accent}`,
        borderRadius: 6,
        background: '#0a0608',
        overflow: 'hidden',
        textAlign: 'center',
      }}>
        {entry?.image_url ? (
          <img src={entry.image_url} alt={entry.note || ''}
            style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', margin: '0 auto', height: 220 }} />
        ) : (
          <div style={{ color: '#a08a4a', lineHeight: '220px' }}>No image</div>
        )}
      </div>

      {/* date */}
      <div style={{
        marginTop: 8, textAlign: 'center',
        fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic',
        fontSize: 12, color: 'rgba(82,46,8,0.7)',
      }}>{dateStr}</div>

      {/* quoted note */}
      {entry?.note && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'rgba(82,46,8,0.08)',
          border: '1px dashed rgba(82,46,8,0.35)',
          borderRadius: 6,
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 13, fontStyle: 'italic', textAlign: 'center',
          color: 'rgba(60,38,8,0.85)',
        }}>“{entry.note}”</div>
      )}

      {/* passage */}
      <div style={{ marginTop: 12 }}>
        <div style={{
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.32em',
          color: 'rgba(82,46,8,0.6)', marginBottom: 4,
        }}>~ Sovereign&apos;s Passage ~</div>
        <div style={{
          background: 'rgba(255,247,220,0.7)',
          border: `1px solid ${accent}88`,
          borderRadius: 6,
          padding: 10,
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 14, lineHeight: 1.5,
          color: passage ? INK : 'rgba(82,46,8,0.45)',
          fontStyle: passage ? 'normal' : 'italic',
          minHeight: 140,
          whiteSpace: 'pre-wrap',
        }}>
          {passage || '— no inscription yet —'}
        </div>
      </div>
    </div>
  );
};

const PrintLedgerPage = ({ entry, idx, total }) => (
  <div style={{
    width: '100%', height: '100%', boxSizing: 'border-box',
    padding: 18, position: 'relative',
    background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
    borderRadius: '4px 14px 14px 4px',
    border: `2px solid ${GOLD}`,
  }}>
    {/* portrait */}
    <div style={{
      width: 140, height: 180, margin: '0 auto',
      border: `2px solid ${GOLD}`, borderRadius: 6,
      background: 'rgba(168,107,31,0.12)',
      overflow: 'hidden', textAlign: 'center',
    }}>
      {entry.image_url ? (
        <img src={entry.image_url} alt={entry.name || ''}
          style={{ display: 'block', width: '100%', height: '100%' }} />
      ) : (
        <div style={{ lineHeight: '180px', color: '#52240a', fontFamily: 'Cormorant Garamond, serif', fontSize: 60 }}>
          {((entry.name || '?')[0] || '?').toUpperCase()}
        </div>
      )}
    </div>
    <p style={{
      marginTop: 12, textAlign: 'center',
      fontFamily: 'Cinzel, Georgia, serif',
      textTransform: 'uppercase', letterSpacing: '0.25em',
      color: INK, fontSize: 18,
    }}>{entry.name || 'Untitled Soul'}</p>
    <div style={{ margin: '8px auto 0', width: 120, height: 1, background: GOLD }} />
    <p style={{
      marginTop: 14, padding: '0 4px',
      fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic',
      fontSize: 15, lineHeight: 1.55, color: 'rgba(26,18,8,0.85)',
      textAlign: (entry.description || '').length > 80 ? 'justify' : 'center',
    }}>{entry.description || '— no inscription yet —'}</p>
    <p style={{
      position: 'absolute', bottom: 8, left: 0, right: 0, textAlign: 'center',
      fontFamily: 'Cinzel, Georgia, serif',
      color: 'rgba(26,18,8,0.45)', fontSize: 9, letterSpacing: '0.3em',
    }}>{idx + 1} of {total}</p>
  </div>
);

const PrintScriptPage = ({ scriptPage, entryTitle, pageIdx, pageTotal }) => (
  <div style={{
    width: '100%', height: '100%', boxSizing: 'border-box',
    padding: 16, position: 'relative',
    background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
    borderRadius: '4px 14px 14px 4px',
    border: `2px solid ${GOLD}`,
  }}>
    {pageIdx === 0 && entryTitle && (
      <div style={{
        textAlign: 'center', marginBottom: 8,
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        textTransform: 'uppercase', letterSpacing: '0.32em', fontSize: 10,
        color: 'rgba(82,46,8,0.7)',
      }}>~ {entryTitle} ~</div>
    )}
    <div style={{
      width: '100%', height: 'calc(100% - 50px)',
      background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 100%)',
      borderRadius: 12,
      overflow: 'hidden',
      padding: 12, boxSizing: 'border-box',
      textAlign: 'center',
    }}>
      {scriptPage?.type === 'hand' && scriptPage.image_data_url ? (
        <img src={scriptPage.image_data_url} alt={entryTitle || 'page'}
          style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', margin: '0 auto' }} />
      ) : (
        <p style={{
          textAlign: 'left',
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: 18, lineHeight: 1.6, color: '#1a1208',
          whiteSpace: 'pre-wrap', margin: 0,
        }}>{scriptPage?.text || ''}</p>
      )}
    </div>
    {pageTotal > 1 && (
      <div style={{
        textAlign: 'center', marginTop: 4,
        fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic',
        fontSize: 11, color: 'rgba(82,46,8,0.55)',
      }}>page {pageIdx + 1} / {pageTotal}</div>
    )}
  </div>
);

const PrintQuotePage = ({ q }) => (
  <div style={{
    width: '100%', height: '100%', boxSizing: 'border-box',
    padding: 24, position: 'relative',
    background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
    borderRadius: '4px 14px 14px 4px',
    border: `2px solid ${GOLD}`,
  }}>
    <p style={{
      textAlign: 'center',
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      textTransform: 'uppercase', letterSpacing: '0.32em', fontSize: 10,
      color: 'rgba(82,46,8,0.7)',
    }}>~ A Royal Decree ~</p>
    <div style={{ margin: '8px auto 16px', width: 80, height: 1, background: GOLD }} />
    {q.vibe_date && (
      <h3 style={{
        textAlign: 'center', margin: '0 0 12px',
        fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 600,
        fontSize: 24, color: INK,
      }}>{q.vibe_date}</h3>
    )}
    <p style={{
      textAlign: 'center', padding: '20px 8px',
      fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic',
      fontSize: 17, lineHeight: 1.5, color: '#2a0e0a',
    }}>“{q.note || ''}”</p>
    <div style={{ position: 'absolute', bottom: 20, left: 0, right: 0, textAlign: 'center' }}>
      <p style={{ margin: 0, fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontSize: 11, color: 'rgba(82,46,8,0.7)' }}>— sealed by</p>
      <p style={{ margin: '2px 0 0', fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700, fontSize: 20, color: INK }}>
        {q.user_nickname || q.user_email || ''}
      </p>
      {q.created_at && (
        <p style={{ margin: '2px 0 0', fontFamily: 'Outfit, sans-serif', fontSize: 10, color: 'rgba(58,20,16,0.55)' }}>
          {new Date(q.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
        </p>
      )}
    </div>
  </div>
);

const PrintCoverPage = ({ empireName, profilePicture, theme }) => (
  <div style={{
    width: '100%', height: '100%', boxSizing: 'border-box',
    padding: 32, position: 'relative', textAlign: 'center',
    background: theme.coverGrad,
    borderRadius: '4px 14px 14px 4px',
    border: `2px solid ${GOLD}`,
  }}>
    <p style={{
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      textTransform: 'uppercase', letterSpacing: '0.42em', fontSize: 11,
      color: '#F5E6A0', marginBottom: 28,
    }}>~ {theme.title} ~</p>
    <div style={{
      width: 110, height: 110, margin: '0 auto 24px',
      borderRadius: '50%', overflow: 'hidden',
      border: '3px solid #D4AF37',
      background: profilePicture ? '#1a0608' : 'radial-gradient(circle at 50% 32%, #f6e6b6, #c69b3f 70%, #6b4612 100%)',
      textAlign: 'center',
    }}>
      {profilePicture ? (
        <img src={profilePicture} alt={empireName}
          style={{ display: 'block', width: '100%', height: '100%' }} />
      ) : (
        <div style={{
          lineHeight: '110px', fontSize: 50, color: '#3a2410',
          fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontWeight: 700,
        }}>{(empireName || 'A')[0].toUpperCase()}</div>
      )}
    </div>
    <h1 style={{
      margin: 0,
      fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700,
      fontSize: 56, color: '#D4AF37', letterSpacing: '0.06em',
      textShadow: '0 1px 0 rgba(0,0,0,0.4)',
    }}>{empireName}</h1>
    <div style={{ margin: '16px auto', width: 120, height: 1, background: GOLD }} />
    <h2 style={{
      margin: 0,
      fontFamily: '"Cormorant Garamond", Georgia, serif', fontStyle: 'italic', fontWeight: 300,
      fontSize: 26, color: '#F5E6A0',
    }}>{theme.title}</h2>
  </div>
);

/* End print-only variants */

/* Read-only Ledger soul entry — mirrors ImperialLedger.CharacterPage
   exactly (rectangular bordered portrait + corner brackets + Cinzel name
   + diamond filigree + italic Cormorant description + page index). */
const LedgerEntryPage = ({ entry, idx, total }) => (
  <PageFrame>
    <div className="relative h-full flex flex-col px-6 pt-6 pb-12 overflow-y-auto">
      {/* Photograph or parchment silhouette */}
      <div className="relative mx-auto mb-3" style={{ width: 130, height: 170 }}>
        <div className="absolute inset-0 rounded-md overflow-hidden"
          style={{
            border: `2px solid ${GOLD}`,
            boxShadow: `0 4px 14px rgba(0,0,0,0.35), inset 0 0 0 1px ${GOLD_LIGHT}77`,
            background: 'rgba(168,107,31,0.12)',
          }}>
          {entry.image_url ? (
            <img src={entry.image_url} alt={entry.name || 'soul'}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <svg viewBox="0 0 60 78" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
              <ellipse cx="30" cy="26" rx="13" ry="14" fill="rgba(82,46,8,0.55)" />
              <path d="M8 78 Q8 50 30 50 Q52 50 52 78 Z" fill="rgba(82,46,8,0.55)" />
            </svg>
          )}
        </div>
        {/* Corner brackets */}
        {[
          { top: -3,    left: -3,    rotate: 0 },
          { top: -3,    right: -3,   rotate: 90 },
          { bottom: -3, right: -3,   rotate: 180 },
          { bottom: -3, left: -3,    rotate: 270 },
        ].map((p, i) => (
          <svg key={i} width="12" height="12" viewBox="0 0 12 12"
            className="absolute" style={{
              top: p.top, left: p.left, right: p.right, bottom: p.bottom,
              transform: `rotate(${p.rotate}deg)`,
            }}>
            <path d="M1 0 V1 H0 V0 Z M0 4 V12 H1 V4 Z M4 0 H12 V1 H4 Z" fill={GOLD} />
          </svg>
        ))}
      </div>

      {/* Name — Cinzel uppercase */}
      <p className="text-center font-['Cinzel'] uppercase tracking-[0.25em]"
        style={{
          color: INK, fontSize: 18,
          textShadow: '0 1px 0 rgba(245,217,122,0.5)',
          wordBreak: 'break-word',
        }}>
        {entry.name || 'Untitled Soul'}
      </p>

      {/* Diamond filigree under name */}
      <div className="mt-2 mx-auto flex items-center gap-2 w-full max-w-[180px]">
        <div className="flex-1 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        <svg width="8" height="8" viewBox="0 0 8 8">
          <path d="M4 0 L5.2 2.8 L8 4 L5.2 5.2 L4 8 L2.8 5.2 L0 4 L2.8 2.8 Z" fill={GOLD} />
        </svg>
        <div className="flex-1 h-px"
          style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
      </div>

      {/* Description — italic Cormorant */}
      <p className="mt-4 font-['Cormorant_Garamond'] italic px-2"
        style={{
          color: 'rgba(26,18,8,0.85)',
          fontSize: 15,
          lineHeight: 1.55,
          textAlign: (entry.description || '').length > 80 ? 'justify' : 'center',
        }}>
        {entry.description || <span style={{ opacity: 0.45 }}>— no inscription yet —</span>}
      </p>

      {/* Page number */}
      <p className="absolute bottom-3 left-0 right-0 text-center font-['Cinzel']"
        style={{ color: 'rgba(26,18,8,0.45)', fontSize: 9, letterSpacing: '0.3em' }}>
        {idx + 1} of {total}
      </p>
    </div>
  </PageFrame>
);

/* Read-only Empire Script page — mirrors NotebookViewer's per-page
   rendering exactly: golden-cream rounded rectangle, full-bleed image
   for 'hand' pages, large Cormorant text for typed pages, wax seal at
   the bottom-right for letters.  We flatten each notebook entry into
   one gallery page per script page so the reader sees the entire
   entry, not just the first page. */
const ScriptPage = ({ scriptPage, isLetter, signatureUrl, entryTitle, pageIdx, pageTotal }) => (
  <PageFrame>
    <div className="relative h-full flex flex-col px-4 pt-4 pb-4">
      {/* Optional title header — only on the first page of the entry */}
      {pageIdx === 0 && entryTitle && (
        <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.32em] text-[10px] text-center mb-2"
          style={{ color: 'rgba(82,46,8,0.7)' }}>
          ~ {entryTitle} ~
        </p>
      )}
      <div className="rounded-xl overflow-hidden relative w-full flex-1"
        style={{
          background: 'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 100%)',
          boxShadow: 'inset 0 0 30px rgba(168,107,31,0.25)',
        }}>
        {scriptPage?.type === 'hand' && scriptPage.image_data_url ? (
          <img
            src={scriptPage.image_data_url}
            alt={entryTitle || 'page'}
            className="w-full h-full object-contain p-3"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full p-5 overflow-y-auto">
            <p className="font-['Cormorant_Garamond'] whitespace-pre-wrap"
              style={{ color: '#1a1208', fontSize: 18, lineHeight: 1.6 }}>
              {scriptPage?.text || ''}
            </p>
          </div>
        )}
        {isLetter && pageIdx === pageTotal - 1 && (
          <div className="absolute bottom-3 right-3">
            <WaxSeal signatureUrl={signatureUrl} size={48} />
          </div>
        )}
      </div>
      {pageTotal > 1 && (
        <p className="text-center font-['Cormorant_Garamond'] italic text-[11px] mt-2"
          style={{ color: 'rgba(82,46,8,0.55)' }}>
          page {pageIdx + 1} / {pageTotal}
        </p>
      )}
    </div>
  </PageFrame>
);

/* Chapter accents — must match SovereignsPath.CHAPTERS so the gallery
   book page looks identical to the original Sovereign editor view. */
const PILLAR_ACCENT = {
  fashion: '#D4AF37',
  food:    '#E8D88C',
  explore: '#0F52BA',
  souls:   '#DC143C',
  sporty:  '#10B981',
  success: '#C0C0C0',
  legacy:  '#8B5CF6',
  art:     '#1E6FE3',
};
const PILLAR_LABEL = {
  fashion: 'Fashion', food: 'Taste', explore: 'Explore', souls: 'Soul',
  sporty: 'Sport', success: 'Success', legacy: 'Legacy', art: 'Art',
};

/* Read-only Memory page — mirrors SovereignsPath.ImagePage exactly.
   Header (page count + path label), framed photo with blurred backdrop,
   datetime, original upload caption in a dashed quoted box, and the
   Sovereign's Passage rendered in the same tan parchment box (but as a
   static read-only paragraph — no textarea or Seal button here, this
   is the gallery's read-only viewer). */
const MemoryPage = ({ entry, pillarId, idx, total }) => {
  const accent = PILLAR_ACCENT[pillarId] || GOLD;
  const label = PILLAR_LABEL[pillarId] || 'Path';
  const dt = entry?.created_at ? new Date(entry.created_at) : null;
  const dateStr = dt ? dt.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }) : '';
  const passage = (entry.passage || '').trim();
  return (
    <PageFrame>
      <div className="relative flex-1 flex flex-col px-4 pt-4 pb-4">
        {/* Chapter header — same row used in SovereignsPath */}
        <div className="flex items-center justify-between">
          <span className="font-['Cormorant_Garamond'] uppercase text-[11px] tracking-[0.28em]"
            style={{ color: 'rgba(82,46,8,0.85)', padding: '6px 10px 6px 4px', fontWeight: 600 }}>
            Index
          </span>
          <div className="font-['Cormorant_Garamond'] uppercase text-[10px] tracking-[0.32em]"
            style={{ color: accent }}>
            {label} Path
          </div>
          <div className="font-['Cormorant_Garamond'] italic text-[11px]"
            style={{ color: 'rgba(82,46,8,0.6)' }}>
            {idx + 1} / {total}
          </div>
        </div>

        {/* Image frame — identical 16:10 frame with blurred backdrop
            + sharp contained foreground (so the whole image is visible). */}
        <div className="mt-3 rounded-md overflow-hidden relative"
          style={{
            border: `2px solid ${accent}`,
            boxShadow: `inset 0 0 0 2px rgba(255,241,184,0.5), 0 0 14px ${accent}55`,
            background: '#0a0608',
            aspectRatio: '16 / 10',
            maxHeight: '36vh',
            width: '100%',
            alignSelf: 'center',
          }}>
          {entry?.image_url ? (
            <>
              <img src={entry.image_url} alt="" aria-hidden draggable={false}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(20px) brightness(0.75)',
                  transform: 'scale(1.15)',
                }} />
              <img src={entry.image_url} alt={entry.note || 'memory'} draggable={false}
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%',
                  objectFit: 'contain',
                }} />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ color: '#a08a4a' }}>
              No image
            </div>
          )}
        </div>

        {/* Date / time stamp */}
        <div className="mt-2 text-center font-['Cormorant_Garamond'] italic text-[12px]"
          style={{ color: 'rgba(82,46,8,0.7)' }}>
          {dateStr}
        </div>

        {/* Original upload caption (read-only quoted box) */}
        {entry?.note && (
          <div className="mt-2 rounded-md px-3 py-2 font-['Cormorant_Garamond'] text-[13px] italic text-center"
            style={{
              background: 'rgba(82,46,8,0.08)',
              border: '1px dashed rgba(82,46,8,0.35)',
              color: 'rgba(60,38,8,0.85)',
            }}>
            “{entry.note}”
          </div>
        )}

        {/* Sovereign's Passage — same tan parchment box, but read-only
            (the gallery is for reading, not editing). Empty-state
            placeholder mirrors the textarea placeholder. */}
        <div className="mt-3 flex-1 flex flex-col">
          <div className="font-['Cormorant_Garamond'] uppercase text-[10px] tracking-[0.32em] mb-1"
            style={{ color: 'rgba(82,46,8,0.6)' }}>
            ~ Sovereign&apos;s Passage ~
          </div>
          <div
            className="w-full flex-1 rounded-md p-3 font-['Cormorant_Garamond'] overflow-y-auto"
            style={{
              background: 'rgba(255,247,220,0.7)',
              border: `1px solid ${accent}88`,
              color: passage ? INK : 'rgba(82,46,8,0.45)',
              fontSize: 14,
              lineHeight: 1.5,
              minHeight: 160,
              whiteSpace: 'pre-wrap',
              fontStyle: passage ? 'normal' : 'italic',
            }}>
            {passage || 'Inscribe new reflections from the Sovereign’s Path tile — they will appear here.'}
          </div>
        </div>
      </div>
    </PageFrame>
  );
};

export const BlissBook = ({ mode = 'quote', onClose }) => {
  const { user } = useAuth();
  const theme = MODE_THEME[mode] || MODE_THEME.quote;
  const empireName = user?.nickname || user?.email?.split('@')[0] || 'Sovereign';

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);          // 0 = cover, 1..N = entries
  const [direction, setDirection] = useState(1);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(theme.feedUrl);
      const list = Array.isArray(data) ? data : [];
      // Reverse: feed returns newest first → we want OLDEST first
      // (the page is the first data the user entered, then second, ..., then last).
      list.sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return da - db;
      });
      setItems(list);
    } catch (e) {
      setItems([]);
    }
  }, [theme.feedUrl]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const printRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const goPrev = () => {
    setDirection(-1);
    setPage((p) => Math.max(0, p - 1));
  };
  const goNext = () => {
    setDirection(1);
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };

  /* ── REAL FILE PDF DOWNLOAD ──
     Captures each page off-screen, composes a landscape A4 PDF with
     two book-pages per sheet (flipbook spread style), then triggers a
     real file download to the user's local disk.  No print dialog —
     the file lands directly in their Downloads folder so they can
     re-open it offline whenever they like. */
  const handleDownload = async () => {
    if (!printRef.current || downloading) return;
    setDownloading(true);
    try {
      const safe = `${empireName}_${theme.subTitle}`.replace(/[^a-z0-9_-]+/gi, '_');
      await exportBookAsFlipbookPdf(printRef.current, safe);
    } catch (e) {
      console.error('[BlissBook] PDF export failed', e);
      // eslint-disable-next-line no-alert
      alert(`Could not generate the PDF on this device.\n\n${e?.message || e}\n\nPlease try again with a stable internet connection.`);
    } finally {
      setDownloading(false);
    }
  };

  /* ── SHARE LINK ──
     Builds a public deep-link to this book and uses the Web Share API
     when available, falling back to clipboard. */
  const handleShare = async () => {
    const url = `${window.location.origin}/share/book/${mode}/${user?.id || ''}`;
    const title = `${empireName} · ${theme.headerLabel}`;
    const text = `Read ${empireName}'s ${theme.subTitle} book on Imperium.`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch (_) { /* user dismissed */ }
    try {
      await navigator.clipboard.writeText(url);
      // eslint-disable-next-line no-alert
      alert('Share link copied to clipboard.');
    } catch (_) {
      // eslint-disable-next-line no-alert
      window.prompt('Copy this link to share:', url);
    }
  };

  /* Flatten the raw items into discrete page-units so each unit
     becomes one flipbook page.  Quote/Ledger/Memory = 1 unit per item.
     Empire Script = one unit per `entry.pages[i]` so multi-page entries
     render every page in the gallery, matching the original viewer. */
  const flatPages = useMemo(() => {
    if (theme.pageKind !== 'script') {
      return items.map((entry) => ({ kind: theme.pageKind, entry }));
    }
    const out = [];
    items.forEach((entry) => {
      const pages = Array.isArray(entry.pages) && entry.pages.length > 0
        ? entry.pages
        : [{ type: 'text', text: entry.note || '' }];
      pages.forEach((p, i) => {
        out.push({ kind: 'script', entry, scriptPage: p, pageIdx: i, pageTotal: pages.length });
      });
    });
    return out;
  }, [items, theme.pageKind]);

  const pillarId = mode.startsWith('pillar-') ? mode.slice(7) : null;
  const totalPages = 1 + flatPages.length;  // 0 = cover, 1..N = flattened page units

  /* Build the current page node */
  const renderUnit = (unit, idx, total) => {
    const author = unit.entry?.user_nickname || unit.entry?.user_email?.split('@')[0] || empireName;
    const sig = (unit.entry?.user_email && unit.entry.user_email === user?.email)
      ? user?.signature_data_url
      : (unit.entry?.signature_data_url || user?.signature_data_url || null);
    if (unit.kind === 'quote') {
      return <QuotePage q={unit.entry} author={author} signatureUrl={sig} theme={theme} />;
    }
    if (unit.kind === 'script') {
      const isLetter = (unit.entry?.category === 'letter');
      return (
        <ScriptPage
          scriptPage={unit.scriptPage}
          isLetter={isLetter}
          signatureUrl={sig}
          entryTitle={unit.entry?.title}
          pageIdx={unit.pageIdx}
          pageTotal={unit.pageTotal}
        />
      );
    }
    if (unit.kind === 'ledger') {
      return <LedgerEntryPage entry={unit.entry} idx={idx} total={total} />;
    }
    return <MemoryPage entry={unit.entry} pillarId={pillarId} idx={idx} total={total} />;
  };

  /* Render a print-optimized unit — used inside the off-screen capture
     container so html2canvas can handle the markup reliably. */
  const renderUnitForPrint = (unit, idx, total) => {
    if (unit.kind === 'quote')  return <PrintQuotePage q={unit.entry} />;
    if (unit.kind === 'script') {
      return <PrintScriptPage scriptPage={unit.scriptPage} entryTitle={unit.entry?.title}
        pageIdx={unit.pageIdx} pageTotal={unit.pageTotal} />;
    }
    if (unit.kind === 'ledger') return <PrintLedgerPage entry={unit.entry} idx={idx} total={total} />;
    return <PrintMemoryPage entry={unit.entry} pillarId={pillarId} idx={idx} total={total} />;
  };

  let pageNode;
  if (page === 0) {
    pageNode = <CoverPage empireName={empireName} profilePicture={user?.profile_picture} theme={theme} />;
  } else {
    const unit = flatPages[page - 1];
    if (unit) pageNode = renderUnit(unit, page - 1, flatPages.length);
  }

  const hint = (() => {
    if (flatPages.length === 0) return theme.emptyHint;
    if (page === 0) return 'tap ▶ to read from the beginning';
    if (page === totalPages - 1) return 'this is your latest entry';
    return 'tap ◀ ▶ to turn the page';
  })();

  return (
    <div
      className="fixed inset-0 z-[150] flex flex-col imperium-shell"
      style={{
        background:
          'radial-gradient(ellipse at top, #1a1208 0%, #060403 60%, #000 100%)',
      }}
      data-testid={`bliss-book-${mode}`}
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 py-3 flex-shrink-0 book-no-print"
        style={{
          paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)',
          borderBottom: '1px solid rgba(212,175,55,0.18)',
        }}>
        <BackPill onClick={onClose} accent="#C8A36A" testId={`close-bliss-book-${mode}`}/>
        <div className="flex flex-col items-center">
          <h2
            className="font-['Cormorant_Garamond'] text-xl font-light tracking-wide flex items-center gap-2"
            style={{ color: GOLD_LIGHT }}>
            <Feather size={16} />
            {theme.headerLabel}
          </h2>
          <p className="font-['Outfit'] text-[10px] tracking-[0.18em] text-white/30 mt-0.5">
            {page === 0
              ? 'COVER'
              : `PAGE ${page} / ${flatPages.length}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleShare}
            className="w-9 h-9 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{
              border: `1px solid ${GOLD}`,
              background: 'rgba(212,175,55,0.08)',
              color: GOLD_LIGHT,
            }}
            data-testid={`bliss-book-${mode}-share`}
            aria-label="Share this book">
            <Share2 size={15} />
          </button>
          <button
            onClick={handleDownload}
            disabled={flatPages.length === 0 || downloading}
            className="w-9 h-9 rounded-full flex items-center justify-center active:scale-90 transition-transform disabled:opacity-30"
            style={{
              border: `1px solid ${GOLD}`,
              background: 'rgba(212,175,55,0.08)',
              color: GOLD_LIGHT,
            }}
            data-testid={`bliss-book-${mode}-download`}
            aria-label="Download as PDF">
            {downloading ? (
              <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <Download size={15} />
            )}
          </button>
        </div>
      </div>

      {/* Book stage */}
      <div className="flex-1 flex items-center justify-center px-3 py-2 min-h-0 book-no-print">
        <div className="relative w-full max-w-md h-full" style={{ perspective: '1800px' }}>
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={page}
              custom={direction}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, rotateY: 0, x: 0 }}
              exit={
                direction > 0
                  ? { rotateY: -110, x: '-12%', opacity: 0 }
                  : { rotateY: 110, x: '12%', opacity: 0 }
              }
              transition={{
                rotateY: { duration: 0.55, ease: [0.65, 0.05, 0.2, 0.95] },
                x:       { duration: 0.55, ease: [0.65, 0.05, 0.2, 0.95] },
                opacity: { duration: 0.35 },
              }}
              style={{
                position: 'absolute', inset: 0,
                transformOrigin: direction > 0 ? 'left center' : 'right center',
                transformStyle: 'preserve-3d',
                boxShadow: '0 8px 28px rgba(0,0,0,0.6)',
              }}>
              {pageNode}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Page-flip controls — circular gold pills (matches QuoteBook).
          Container above already uses 100svh which sizes to the SMALLEST
          visible viewport, so we only need a small visual breathing-room
          padding here, not a tall floor. */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-6 pt-1 book-no-print"
        style={{
          paddingBottom: 'calc(56px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
          boxSizing: 'border-box',
        }}>
        <button
          onClick={goPrev}
          disabled={page === 0}
          className="w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-25 active:scale-90 transition-transform"
          style={{
            border: `1.5px solid ${GOLD}`,
            background: 'rgba(212,175,55,0.08)',
            color: GOLD_LIGHT,
            boxShadow: '0 0 14px rgba(212,175,55,0.25)',
          }}
          data-testid={`bliss-book-${mode}-prev`}>
          <ChevronLeft size={22} />
        </button>

        <p className="font-['Cormorant_Garamond'] italic text-xs text-white/40 text-center px-2">
          {hint}
        </p>

        <button
          onClick={goNext}
          disabled={page >= totalPages - 1}
          className="w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-25 active:scale-90 transition-transform"
          style={{
            border: `1.5px solid ${GOLD}`,
            background: 'rgba(212,175,55,0.08)',
            color: GOLD_LIGHT,
            boxShadow: '0 0 14px rgba(212,175,55,0.25)',
          }}
          data-testid={`bliss-book-${mode}-next`}>
          <ChevronRight size={22} />
        </button>
      </div>

      {/* ═══════════ OFF-SCREEN PAGE CAPTURE CONTAINER ═══════════
          Always mounted (positioned far off-screen) so html2canvas can
          snapshot every page when the user clicks Download.  Each page
          is sized at 100mm × 180mm (≈ phone-book) and rendered at the
          standard 96dpi pixel mapping (≈ 378px × 680px) so the snapshot
          is sharp inside the final landscape PDF spread. */}
      <div ref={printRef} aria-hidden
        style={{
          position: 'fixed', left: -10000, top: 0,
          width: '378px',
          background: 'transparent',
          pointerEvents: 'none',
        }}
        data-testid={`bliss-book-${mode}-print`}>
        <div className="book-print-page"
          style={{ width: '378px', height: '680px', position: 'relative' }}>
          <PrintCoverPage empireName={empireName} profilePicture={user?.profile_picture} theme={theme} />
        </div>
        {flatPages.map((unit, i) => (
          <div key={i} className="book-print-page"
            style={{ width: '378px', height: '680px', position: 'relative' }}>
            {renderUnitForPrint(unit, i, flatPages.length)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlissBook;
